import type { AccountStatus, CompanyOutcome } from '@nestjs-boilerplate/types';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  useApproveUser,
  useCompanyOutcome,
  useDisableUser,
  useEnableUser,
  useRejectUser,
} from '../api/users';

type ActionKind = 'approve' | 'reject' | 'disable' | 'enable';

/** One button offered from the current account status. */
interface ActionConfig {
  kind: ActionKind;
  label: string;
  description: string;
  /** Reject and disable are the audit answer to "why" — the API rejects a blank one anyway. */
  requiresReason: boolean;
}

/**
 * Which buttons are legal from each status.
 *
 * Only transitions the API will actually accept — offering an illegal one
 * just invites a 409. Rejection is terminal in this ticket, so REJECTED
 * offers nothing.
 */
const ACTIONS_BY_STATUS: Record<AccountStatus, ActionConfig[]> = {
  PENDING: [
    {
      kind: 'approve',
      label: 'Approve',
      description: 'Approve this applicant and grant them access?',
      requiresReason: false,
    },
    {
      kind: 'reject',
      label: 'Reject',
      description: 'This applicant will be turned away. This cannot be undone from here.',
      requiresReason: true,
    },
  ],
  APPROVED: [
    {
      kind: 'disable',
      label: 'Disable',
      description: 'This account will lose access immediately.',
      requiresReason: true,
    },
  ],
  DISABLED: [
    {
      kind: 'enable',
      label: 'Enable',
      description: 'Restore this account to approved status?',
      requiresReason: false,
    },
  ],
  REJECTED: [],
};

/**
 * What approving will do with the company the applicant claimed.
 *
 * The second sentence of the attach notice is not decoration. Sharing a balance
 * is the consequence that makes this decision worth a human, and the modal says
 * so rather than assuming the administrator has it in mind.
 */
function CompanyNotice({
  outcome,
  isLoading,
}: {
  outcome: CompanyOutcome | undefined;
  isLoading: boolean;
}) {
  if (isLoading) {
    return <p className="text-muted-foreground text-sm">Checking the company details…</p>;
  }

  if (!outcome || outcome.kind === 'none') {
    return null;
  }

  if (outcome.kind === 'ambiguous') {
    return (
      <p className="rounded-md bg-destructive/10 p-3 text-destructive text-sm">
        More than one company holds the number <strong>{outcome.companyNumber}</strong>. Resolve the
        duplicate before approving this applicant.
      </p>
    );
  }

  return (
    <p className="rounded-md bg-muted p-3 text-sm">
      {outcome.kind === 'attach' ? (
        <>
          These details match{' '}
          <strong>
            {outcome.companyName}
            {outcome.companyNumber ? ` (${outcome.companyNumber})` : ''}
          </strong>
          . Approving will add this user to that company, and they will share its balance.
        </>
      ) : (
        <>
          No existing company matches these details. Approving will create{' '}
          <strong>{outcome.companyName}</strong> as a new company.
        </>
      )}
    </p>
  );
}

/**
 * Approve, reject, disable or re-enable a user, right from their record.
 *
 * Renders only the actions legal from the current status. Reject and disable
 * open a confirmation dialog with a required reason; approve shows what it will
 * do with the company the applicant claimed, because attaching someone to an
 * existing firm grants them spending rights over its pooled balance.
 */
export function UserStatusActions({
  userId,
  accountStatus,
}: {
  userId: string;
  accountStatus: AccountStatus;
}) {
  const approve = useApproveUser();
  const reject = useRejectUser();
  const disable = useDisableUser();
  const enable = useEnableUser();

  const [openKind, setOpenKind] = useState<ActionKind | null>(null);
  const [reason, setReason] = useState('');

  const actions = ACTIONS_BY_STATUS[accountStatus];
  const active = actions.find((action) => action.kind === openKind) ?? null;

  // Only while the approve dialog is open — resolving on every render would
  // query for a decision nobody is making.
  const companyOutcome = useCompanyOutcome(userId, openKind === 'approve');

  if (actions.length === 0) {
    return null;
  }

  function closeDialog() {
    setOpenKind(null);
    setReason('');
  }

  function handleConfirm(kind: ActionKind) {
    // Close only once the mutation actually succeeds. Closing unconditionally
    // here made a 403 (an administrator disabling themselves) look like it
    // had worked — the dialog vanished and the failure only showed up a beat
    // later as a toast. For a decision that gates access to a compliance
    // platform, "appeared to succeed" is the wrong default.
    const options = { onSuccess: closeDialog };
    if (kind === 'approve') {
      // Sends back the outcome the administrator was shown, and the firm it
      // named. The server re-resolves and refuses if either has changed since
      // the modal opened — the id matters because a firm can lose its number
      // to another while the modal is open, leaving the kind unchanged.
      approve.mutate(
        {
          id: userId,
          expectedOutcome: companyOutcome.data?.kind,
          expectedCompanyId: companyOutcome.data?.companyId,
        },
        options,
      );
    } else if (kind === 'enable') {
      enable.mutate(userId, options);
    } else if (kind === 'reject') {
      reject.mutate({ id: userId, reason }, options);
    } else {
      disable.mutate({ id: userId, reason }, options);
    }
  }

  const isPending =
    active?.kind === 'approve'
      ? approve.isPending
      : active?.kind === 'reject'
        ? reject.isPending
        : active?.kind === 'disable'
          ? disable.isPending
          : active?.kind === 'enable'
            ? enable.isPending
            : false;

  const reasonBlank = reason.trim().length === 0;
  // Approving is blocked until the company decision is known, and outright when
  // it cannot be made — confirming an unresolved outcome is exactly the silent
  // attachment this design prevents.
  const companyBlocked =
    active?.kind === 'approve' &&
    (companyOutcome.isPending || companyOutcome.data?.kind === 'ambiguous');
  const confirmDisabled = (active?.requiresReason && reasonBlank) || isPending || companyBlocked;

  return (
    <>
      <div className="flex items-center gap-2">
        {actions.map((action) => (
          <Button
            key={action.kind}
            type="button"
            variant={action.requiresReason ? 'outline' : 'default'}
            size="sm"
            onClick={() => setOpenKind(action.kind)}
          >
            {action.label}
          </Button>
        ))}
      </div>

      <Dialog
        open={active !== null}
        onOpenChange={(next) => {
          if (!next) closeDialog();
        }}
      >
        <DialogContent>
          {active && (
            <>
              <DialogHeader>
                <DialogTitle>{active.label}</DialogTitle>
                <DialogDescription>{active.description}</DialogDescription>
              </DialogHeader>

              {active.kind === 'approve' && (
                <CompanyNotice outcome={companyOutcome.data} isLoading={companyOutcome.isPending} />
              )}

              {active.requiresReason && (
                <div className="space-y-2">
                  <Label htmlFor="user-status-action-reason">Reason</Label>
                  <Textarea
                    id="user-status-action-reason"
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                  />
                </div>
              )}

              <DialogFooter>
                <Button
                  type="button"
                  variant={active.requiresReason ? 'destructive' : 'default'}
                  disabled={confirmDisabled}
                  onClick={() => handleConfirm(active.kind)}
                >
                  {isPending ? 'Working…' : 'Confirm'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
